import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { useRef, useCallback } from 'react';
import { streamApi, RateLimitError } from '../api.js';
import { getHeadersWithUniqueVisitorId } from '../../Auth/index.js';
import { processProductGroupTags, extractStreamingProductGroups } from '../utils/productGroupParser.js';

const useStreamingSearch = ({ brandId, appConfigId, conversationState, scrollToBottom, shouldScrollOnNextMessageRef, onStreamError, }) => {
    const { conversationId, setConversationId, setIsActivelyStreaming, setStreamingAIResponse, setCurrentStatus, setConversationHistory, setIsWaitingForFollowUpQueries, setStreamingProductGroups, setProductMetadataMap, setPreviewSearchQueries, setPreviewQueryProducts, setFollowUpQuestions, setStreamingFeaturedProduct, setStreamingStoreLocator, clearStreamingState, apiBaseUrl, } = conversationState;
    const abortControllerRef = useRef(null);
    const cancelStream = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const performSearch = useCallback((searchTerm, imageData, imageMimeType, skipAddingUserMessage) => __awaiter(void 0, void 0, void 0, function* () {
        if (!searchTerm.trim() && !imageData) {
            setStreamingAIResponse('');
            return;
        }
        // Create new abort controller for this search
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        // Set actively streaming flag BEFORE any state changes
        setIsActivelyStreaming(true);
        // Add user message to conversation history immediately (unless skipped)
        shouldScrollOnNextMessageRef.current = true;
        if (!skipAddingUserMessage) {
            // Check if this is a featured product query to extract the question
            const featuredProductRegex = /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
            const featuredMatch = searchTerm.match(featuredProductRegex);
            const displayContent = featuredMatch && featuredMatch[2] ? featuredMatch[2] : searchTerm;
            const userMessage = Object.assign({ role: 'user', content: displayContent, timestamp: new Date() }, (imageData && {
                imageData,
                imageMimeType,
            }));
            setConversationHistory((prev) => [...prev, userMessage]);
        }
        setStreamingAIResponse('');
        setIsWaitingForFollowUpQueries(false);
        setStreamingProductGroups(new Map());
        // Clear preview data immediately when new search starts to prevent old data from showing
        setPreviewSearchQueries([]);
        setPreviewQueryProducts([]);
        let fullBuffer = '';
        let currentProducts = [];
        let currentFollowUpQuestions = [];
        let featuredProductData = null;
        let storeLocatorData = null;
        let capturedOrders = [];
        let capturedSearchQueries = [];
        let capturedQueryProducts = [];
        try {
            const headers = yield getHeadersWithUniqueVisitorId();
            yield streamApi({
                prompt: searchTerm,
                brandId: brandId || undefined,
                appConfigId: appConfigId || undefined,
                base64Image: imageData,
                imageMimeType: imageMimeType,
                // imageUrl is now optional - backend will upload if not provided
                conversationId: conversationId || undefined,
                headersToInject: headers,
                apiBaseUrl,
                signal: abortController.signal,
                onStatusChange: (status) => {
                    setCurrentStatus(status);
                },
                onConversationId: (id) => {
                    setConversationId(id);
                },
                onNewProductMetadata: (products) => {
                    setProductMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        products.forEach((product) => {
                            newMap.set(product.stacklineSku, product);
                        });
                        return newMap;
                    });
                    currentProducts = [...currentProducts, ...products];
                    setStreamingAIResponse(processProductGroupTags(fullBuffer));
                },
                onFeaturedProduct: (product, question) => {
                    // Store featured product data for later (when stream completes)
                    featuredProductData = { product, question };
                    // Add product to metadata map
                    setProductMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(product.stacklineSku, product);
                        return newMap;
                    });
                    // IMMEDIATELY set streaming featured product to show widget below user message
                    setStreamingFeaturedProduct({ product, question });
                    // Scroll to show the widget
                    scrollToBottom();
                },
                onStoreLocator: (stacklineSku) => {
                    // Store store locator data for later (when stream completes)
                    storeLocatorData = { stacklineSku };
                    // IMMEDIATELY set streaming store locator to show widget
                    setStreamingStoreLocator({ stacklineSku });
                    // Scroll to show the widget
                    scrollToBottom();
                },
                onOrders: (orders) => {
                    // Store orders for inclusion in the final message
                    capturedOrders = orders;
                },
                onEvent: (event) => {
                    if (event.name === 'search_products_queries') {
                        capturedSearchQueries = event.data;
                        setPreviewSearchQueries(event.data);
                    }
                    else if (event.name === 'search_query_products') {
                        // Append to our array of query products
                        capturedQueryProducts = [...capturedQueryProducts, event.data];
                        setPreviewQueryProducts(capturedQueryProducts);
                    }
                    else if (event.name === 'follow_up_queries') {
                        currentFollowUpQuestions = event.data;
                        setIsWaitingForFollowUpQueries(false);
                    }
                },
                onChunk: (chunk) => {
                    try {
                        const data = JSON.parse(chunk);
                        if (data.type === 'follow_up_questions' && data.content) {
                            currentFollowUpQuestions = data.content;
                            return;
                        }
                        else if (Array.isArray(data)) {
                            return;
                        }
                    }
                    catch (_a) {
                        // Not JSON, treat as text
                    }
                    if (chunk.includes('Stream completed.')) {
                        return;
                    }
                    fullBuffer += chunk;
                    const newStreamingGroups = extractStreamingProductGroups(fullBuffer);
                    if (newStreamingGroups.size > 0) {
                        setStreamingProductGroups(newStreamingGroups);
                    }
                    const processedText = processProductGroupTags(fullBuffer);
                    setStreamingAIResponse(processedText);
                    scrollToBottom();
                },
                onComplete: () => {
                    // Clear streaming state BEFORE adding the finalized message to history.
                    // This prevents both the streaming message AND finalized message being rendered.
                    // Note: setIsActivelyStreaming(false) is handled in the finally block.
                    clearStreamingState();
                    // NOW add the finalized message to history (after streaming state is cleared)
                    const assistantMessage = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ role: 'assistant', content: fullBuffer, timestamp: new Date(), products: currentProducts }, (featuredProductData && {
                        featuredProduct: featuredProductData,
                    })), (storeLocatorData && {
                        storeLocator: storeLocatorData,
                    })), (capturedOrders.length > 0 && {
                        orders: capturedOrders,
                    })), (capturedSearchQueries.length > 0 && {
                        previewSearchQueries: capturedSearchQueries,
                    })), (capturedQueryProducts.length > 0 && {
                        previewQueryProducts: capturedQueryProducts,
                    })), (currentFollowUpQuestions.length > 0 && {
                        followUpQueries: currentFollowUpQuestions,
                    }));
                    setConversationHistory((prev) => [...prev, assistantMessage]);
                    setFollowUpQuestions(currentFollowUpQuestions);
                    if (currentFollowUpQuestions.length === 0) {
                        setIsWaitingForFollowUpQueries(true);
                        setTimeout(() => {
                            setIsWaitingForFollowUpQueries(false);
                        }, 3000);
                    }
                },
                onError: (error) => {
                    var _a, _b, _c, _d;
                    console.error('Stream failed:', error);
                    setCurrentStatus('');
                    // Remove the user message that was just added since the request failed
                    if (!skipAddingUserMessage) {
                        setConversationHistory((prev) => prev.slice(0, -1));
                    }
                    // Determine error type for appropriate user messaging
                    let errorType = 'unknown';
                    if (error instanceof RateLimitError) {
                        errorType = 'rate_limit';
                    }
                    else if (error instanceof TypeError || ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('network')) || ((_b = error.message) === null || _b === void 0 ? void 0 : _b.includes('fetch'))) {
                        errorType = 'network';
                    }
                    else if (((_c = error.message) === null || _c === void 0 ? void 0 : _c.includes('5')) && ((_d = error.message) === null || _d === void 0 ? void 0 : _d.includes('status'))) {
                        // 5xx server errors
                        errorType = 'server';
                    }
                    // Call callback so container can restore the input and show appropriate error toast
                    onStreamError === null || onStreamError === void 0 ? void 0 : onStreamError(errorType, searchTerm);
                },
            });
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setIsActivelyStreaming(false);
        }
    }), [
        brandId,
        appConfigId,
        conversationId,
        scrollToBottom,
        setConversationHistory,
        setConversationId,
        setCurrentStatus,
        setFollowUpQuestions,
        setIsActivelyStreaming,
        setIsWaitingForFollowUpQueries,
        setPreviewQueryProducts,
        setPreviewSearchQueries,
        setProductMetadataMap,
        setStreamingAIResponse,
        setStreamingProductGroups,
        setStreamingFeaturedProduct,
        setStreamingStoreLocator,
        clearStreamingState,
        shouldScrollOnNextMessageRef,
        apiBaseUrl,
        onStreamError,
    ]);
    return { performSearch, cancelStream };
};

export { useStreamingSearch };
//# sourceMappingURL=useStreamingSearch.js.map
